import * as React from 'react';
import { cn } from '@/shared/lib/utils';
import type { TruncatedTextProps } from '@/shared/types/truncated-text.types';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './tooltip';

/**
 * TruncatedText - Accessible truncated text with tooltip
 *
 * Wraps text that may be truncated with a Tooltip that reveals
 * the full content on hover (desktop) or tap (mobile).
 *
 * The component uses the existing Tooltip which automatically
 * renders as a Popover on touch devices for better accessibility.
 *
 * @example
 * // Single line truncation
 * <TruncatedText className="max-w-[200px]">
 *   Very long text that will be truncated with ellipsis
 * </TruncatedText>
 *
 * @example
 * // Multi-line truncation (2 lines)
 * <TruncatedText lines={2} maxWidth="max-w-xs">
 *   Long description that spans multiple lines before truncating
 *   with an ellipsis at the end of the second line.
 * </TruncatedText>
 *
 * @example
 * // With custom typography
 * <TruncatedText
 *   lines={1}
 *   className="text-sm text-muted-foreground font-medium"
 *   maxWidth="max-w-[180px]"
 * >
 *   Event location or subtitle text
 * </TruncatedText>
 */
export function TruncatedText({
  children,
  className,
  lines = 1,
  maxWidth = 'max-w-full',
  alwaysShowTooltip = true,
}: TruncatedTextProps) {
  // Determine the appropriate truncation class
  const lineClampClass = React.useMemo(() => {
    switch (lines) {
      case 1:
        return 'truncate';
      case 2:
        return 'line-clamp-2';
      case 3:
        return 'line-clamp-3';
      default:
        return 'truncate';
    }
  }, [lines]);

  // If tooltip is disabled or children is empty, render plain text
  if (!alwaysShowTooltip || !children) {
    return (
      <span className={cn(lineClampClass, maxWidth, className)}>
        {children}
      </span>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              lineClampClass,
              maxWidth,
              'cursor-default',
              className
            )}
          >
            {children}
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs break-words">
          {children}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

TruncatedText.displayName = 'TruncatedText';
